<template>
  <!-- A drawer of its own, which is what every host but one wants. -->
  <BaseSideModal
    v-if="!embedded"
    ref="modal"
    :width="width"
    :show-footer="showFooter"
    :confirm-close-dialog="confirmCloseDialog"
    @close="$emit('close')"
  >
    <template #title><slot name="title"/></template>
    <slot/>
    <template v-if="showFooter" #footer><slot name="footer"/></template>
  </BaseSideModal>

  <!-- Or the same screen inside the drawer that led here, which is where a review reached from a
       list belongs: a modal over a drawer buries the list it came from and offers no way back to
       it except closing. The breadcrumb is that way back. -->
  <div v-else class="review-shell">
    <p class="review-shell__header">
      <button
        type="button"
        class="review-shell__back"
        :title="backLabel"
        @click="closeModal"
      >←</button>
      <span class="review-shell__title"><slot name="title"/></span>
    </p>

    <div class="review-shell__content"><slot/></div>

    <div v-if="showFooter" class="review-shell__footer"><slot name="footer"/></div>

    <!-- The drawer's own question, asked the same way and answered the same way round: confirm
         keeps you here, the other one leaves. It says Back, not Close: leaving an embedded review
         returns to the list it was opened from rather than closing anything. -->
    <BaseDialog
      v-if="confirmDialogOpen"
      :width="'444px'"
      :text-align="'left'"
      :footer-align="'flex-end'"
      :title="$t('adminTitles.AreYouSureToCancelOperation')"
      :text="$t('adminTitles.AllUnsavedDataWillBeLost')"
      :confirm-button-text="$t('buttons.KeepEditing')"
      :cancel-button-text="$t('buttons.back')"
      @close="leave"
      @confirm="confirmDialogOpen = false"
    />
  </div>
</template>

<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';

/**
 * The review's outer frame — a side modal, or the body of one it is already inside.
 *
 * Not a style switch. A drawer that opens a modal over itself hides the list the operator was
 * working through, and the only way back is to close everything; rendered in place, the list is
 * one click behind a breadcrumb and still there when the group is finished.
 *
 * A wrapper rather than two copies of the review's template: PaymentsReviewModal is a long file
 * and the difference between the two hosts is entirely this frame.
 *
 * Exposes `closeModal()` because the review's own Back button calls it, so both ways out ask the
 * same question — which is the whole reason BaseSideModal exposes it too.
 */
export default {
  name: 'ReviewShell',
  components: { BaseSideModal, BaseDialog },
  emits: ['close'],
  props: {
    /** Render in place instead of as a drawer of its own. */
    embedded: {
      type: Boolean,
      default: false,
    },
    /** What the breadcrumb goes back to, e.g. "All updates". Embedded only. */
    backLabel: {
      type: String,
      default: '',
    },
    width: {
      type: String,
      default: '500px',
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    confirmCloseDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { confirmDialogOpen: false };
  },
  methods: {
    /** Both ways out — the breadcrumb and the review's Back button — come through here. */
    closeModal() {
      if (this.embedded) {
        if (this.confirmCloseDialog) this.confirmDialogOpen = true;
        else this.leave();
        return;
      }
      this.$refs.modal.closeModal();
    },
    leave() {
      this.confirmDialogOpen = false;
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
/* No height and no scroller of its own. The drawer this renders inside already scrolls its
   content, and a second scroll area inside the first gives two scrollbars and a footer that
   strands itself halfway up the window. */
.review-shell {
  display: flex;
  flex-direction: column;
}

.review-shell__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--solid-lavender-grey);
  font-weight: 700;
  font-size: 16px;
  line-height: 1.35;
  color: var(--solid-black-purple);
}

/* Borderless and purple, as the one other two-view drawer in the app draws it. */
.review-shell__back {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  color: var(--solid-purple);

  &:hover { opacity: 0.7; }
}

.review-shell__title {
  min-width: 0;
}

/* The drawer's own footer arrangement, so the buttons sit where they always do and
   `.review-updating { margin-right: auto }` still pushes the status text left. Sticky rather
   than fixed: Apply has to stay reachable down a long group without taking a scroll area of its
   own, and the drawer's background is what it sits on. */
.review-shell__footer {
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  margin-top: 12px;
  background: #ffffff;
  box-shadow: 0px -4px 4px 0px #00000005;
}
</style>
